import React from 'react';
import _ from 'lodash';
import { NextSeo } from 'next-seo';
import classnames from 'classnames';
import { parse } from 'url';

import { ContentContext, LayoutLocationContext } from '@/globals/contexts';
import ApiClient from '@/utils/ApiClient';
import { replaceFields } from '@/utils/misc';
import { getRootStyles } from '@/helpers/DOM/css';
import { isDev } from '@/helpers/env';

import ContentStructuredDataHeader from '@/pageComponents/SEO/ContentStructuredDataHeader';
import Layout from '@/Layout';

const fetchContentPage = async (ctx, { contentId, contentType }) => {
  try {
    const apiClient = ctx?.apiForServer || ApiClient;
    const path = `${apiClient.instanceName}/rendering/pages/forContent`;

    return await apiClient._get(path, { contentId, contentType });
  } catch (e) {
    console.log(e);
    return null;
  }
};

/**
 * Will be rendered as Component inside AppComponent
 */

export default class ContentPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      page: props.page,
      content: props.content,
      layout: props.layout,
    };
  }

  static async getInitialProps(ctx) {
    const { pathname, search } = parse(ctx.asPath);
    const contentPage = await fetchContentPage(ctx, ctx.query);

    const { content } = contentPage;

    if (pathname !== content?.path && ctx.res) {
      ctx.res.writeHead(isDev() ? 302 : 301, {
        Location: content.path + (search || ''),
      });
      ctx.res.end();
    }

    return contentPage;
  }

  componentDidMount() {
    const { incrementPaywallView, content } = this.props;

    window.setTimeout(() => incrementPaywallView(content), 1000);
  }

  async componentWillReceiveProps(nextProps, previousProps) {
    const { content } = this.state;
    if (nextProps.user && !previousProps.user) {
      const contentPage = await fetchContentPage(null, {
        contentId: content._id,
        contentType: content._cls,
      });
      this.setState({ content: contentPage.content });
    }
  }

  recordLocalView(entity) {
    const { paywallContext } = this.props;
    paywallContext.notifiyView(entity);
  }

  render() {
    const { baseUrl, variables = {}, siteContext, apiContext } = this.props;
    const { getImageUrl } = apiContext;
    let { page, content, layout } = this.state;
    const {
      siteConfig: { options: siteOptions },
    } = siteContext;

    if (!page?.templateLayout || !content) return null;

    page = _.mapValues(page, (v) =>
      _.isString(v)
        ? replaceFields(v, {
            ...variables,
            label: content.seoLabel || content.label,
          })
        : v
    );

    // SEO
    const imageForSharing = content.imageForSharing || content.image;
    const canonicalUrl = `${baseUrl}${content.path}`;
    const sharingImageUrl = getImageUrl(imageForSharing);

    const twitterSharingImageUrl =
      (imageForSharing && getImageUrl(imageForSharing)) || '';

    // Classes
    const className = classnames('Page', page.className);
    const wrapperClassName = classnames(className, {
      [`content-type-${content._cls}`]: content._cls,
    });

    return (
      <div
        className={wrapperClassName}
        style={getRootStyles(page, siteOptions)}
      >
        <NextSeo
          title={content.seoLabel}
          canonical={canonicalUrl}
          description={content.seoDescription}
          noindex={content.disableSeoIndexation || page.disableSeoIndexation}
          nofollow={content.disableSeoIndexation || page.disableSeoIndexation}
          openGraph={{
            title: content.socialNetworkLabel,
            description: content.socialNetworkDescription,
            images: [
              {
                url: sharingImageUrl,
              },
            ],
          }}
          twitter={{
            cardType: 'summary_large_image',
            image: twitterSharingImageUrl,
          }}
        />

        <ContentStructuredDataHeader content={content} />

        <div
          className={className + '-body Page-body Page--' + page._id}
          role="main"
        >
          <LayoutLocationContext.Provider
            value={{
              source: `ContentPage:${page._id}:templateLayout`,
            }}
          >
            <ContentContext.Provider value={content}>
              <Layout layout={layout} isPageWrapper />
            </ContentContext.Provider>
          </LayoutLocationContext.Provider>
        </div>
      </div>
    );
  }
}
